import React, { useMemo } from "react";
import { Card, Col, Placeholder, Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useFetchAbsencesQuery } from "features/absences/absenceSlice";
import { useFetchClassesQuery } from "features/classes/classeSlice";

const AbsencesToday = () => {
  const { data: absences = [], isLoading, isError } = useFetchAbsencesQuery();
  const { data: classes = [] } = useFetchClassesQuery();

  const classMap = useMemo(() => {
    const map: Record<string, string> = {};
    classes.forEach((c: any) => {
      if (c._id) map[c._id] = c.nom_classe || c.nom || c._id;
    });
    return map;
  }, [classes]);

  const today = new Date().toISOString().slice(0, 10);

  const todayAbsences = useMemo(() => {
    return absences.filter((a) => {
      if (!a.date) return false;
      const absDate = a.date.slice(0, 10);
      return absDate === today;
    });
  }, [absences, today]);

  const totalAbsentsToday = useMemo(() => {
    return todayAbsences.reduce((sum, a) => sum + (a.eleves?.length || 0), 0);
  }, [todayAbsences]);

  const classeStats = useMemo(() => {
    const map: Record<string, number> = {};
    todayAbsences.forEach((a) => {
      const key = a.classe || "Inconnue";
      map[key] = (map[key] || 0) + (a.eleves?.length || 0);
    });
    return Object.entries(map)
      .map(([classeId, count]) => ({
        name: classMap[classeId] || classeId,
        count,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [todayAbsences, classMap]);

  const thisMonth = new Date().getMonth();
  const thisYear = new Date().getFullYear();
  const monthAbsences = useMemo(() => {
    return absences.filter((a) => {
      if (!a.date) return false;
      const d = new Date(a.date);
      return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
    });
  }, [absences, thisMonth, thisYear]);

  const totalAbsentsMonth = useMemo(() => {
    return monthAbsences.reduce((sum, a) => sum + (a.eleves?.length || 0), 0);
  }, [monthAbsences]);

  return (
    <Col xxl={6} lg={12}>
      <Card className="card-height-100">
        <Card.Header className="d-flex align-items-center">
          <h5 className="card-title mb-0 flex-grow-1">
            <i className="ri-calendar-check-line me-2 text-danger" />
            Absences du jour
          </h5>
          <Link to="/absences" className="btn btn-soft-primary btn-sm">
            Voir tout <i className="ri-arrow-right-s-line" />
          </Link>
        </Card.Header>
        <Card.Body>
          {isLoading ? (
            <Placeholder as="div" animation="glow">
              <Placeholder xs={12} className="mb-2" style={{ height: 40 }} />
              <Placeholder xs={8} className="mb-2" />
              <Placeholder xs={6} />
            </Placeholder>
          ) : isError ? (
            <div className="text-center text-danger py-3">
              <i className="ri-error-warning-line fs-2 d-block mb-2" />
              Impossible de charger les absences
            </div>
          ) : (
            <>
              <div className="d-flex gap-3 mb-3">
                <div className="flex-fill text-center p-3 rounded bg-danger-subtle">
                  <h3 className="text-danger mb-1">{totalAbsentsToday}</h3>
                  <p className="text-muted mb-0 fs-13">Absents aujourd'hui</p>
                </div>
                <div className="flex-fill text-center p-3 rounded bg-warning-subtle">
                  <h3 className="text-warning mb-1">{totalAbsentsMonth}</h3>
                  <p className="text-muted mb-0 fs-13">Ce mois</p>
                </div>
                <div className="flex-fill text-center p-3 rounded bg-info-subtle">
                  <h3 className="text-info mb-1">{todayAbsences.length}</h3>
                  <p className="text-muted mb-0 fs-13">Séances touchées</p>
                </div>
              </div>

              {classeStats.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-sm table-borderless align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="fs-13">Classe</th>
                        <th className="fs-13 text-end">Absents</th>
                      </tr>
                    </thead>
                    <tbody>
                      {classeStats.map((c, i) => (
                        <tr key={i}>
                          <td>
                            <i className="ri-group-line me-1 text-muted" />
                            {c.name}
                          </td>
                          <td className="text-end">
                            <Badge
                              bg={c.count >= 5 ? "danger" : c.count >= 3 ? "warning" : "info"}
                              className="fs-12"
                            >
                              {c.count}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center text-success py-3">
                  <i className="ri-checkbox-circle-line fs-2 d-block mb-2" />
                  Aucune absence enregistrée aujourd'hui
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </Col>
  );
};

export default AbsencesToday;
